import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { ConditionalRendering } from '../conditional-rendering';
// hook
import { useCombobox } from 'downshift';
import { useDebounce } from '@containers/common/useDebounce';
interface IAutocompleteSearch {
    id: string;
    selectedItems: Array<any>;
    label: string;
    isClearInputValue?: boolean;
    setSelectedItems: (val: any) => void;
    fetchResults: (val: string) => Promise<any> | any[];
    debounceSearchTime?: number;
    formatMessageObject?: {};
    minSearchQueryLength?: number;
    loadDataOnInit?: boolean;
    isClearApiResults?: boolean;
    placeHolder?: string;
    idKey?: string;
    valueKey?: string;
    resetTrigger?: boolean;
    isSingleSelect?: boolean;
    initialSearchInputValue?: string;
    disabled?: boolean;
}
const INITIAL_SEARCH_VALUE = '';
const AutocompleteSearch = (props: IAutocompleteSearch) => {
    const {
        id = '',
        setSelectedItems,
        selectedItems = [],
        label = '',
        isClearInputValue = false,
        fetchResults,
        debounceSearchTime = 0,
        formatMessageObject = {},
        minSearchQueryLength = 1,
        loadDataOnInit = false,
        isClearApiResults = false,
        placeHolder = '',
        idKey = 'id',
        valueKey = 'name',
        resetTrigger = false,
        isSingleSelect = false,
        disabled = false,
        initialSearchInputValue = INITIAL_SEARCH_VALUE,
    } = props;

    const intl = useIntl();
    const INPUT_CHANGE = '__input_change__';
    const INPUT_CHANGE_EVENT_CODE = 8;
    const [searchValue, setSearchValue] = useState(initialSearchInputValue);
    const [searchAPIResult, setSearchAPIResult] = useState([]);

    const debounceSearchValue: string = useDebounce(searchValue, debounceSearchTime);

    const { isOpen, getMenuProps, getInputProps, getItemProps, highlightedIndex, reset, setInputValue, inputValue } =
        useCombobox({
            labelId: `${id}-label`,
            items: searchAPIResult,
            itemToString: item => (item ? item[valueKey] : ''),
            onInputValueChange: props => {
                const { inputValue, type } = props || '';
                if ([INPUT_CHANGE, INPUT_CHANGE_EVENT_CODE].includes(type)) {
                    setSearchValue(inputValue);
                }
                if (loadDataOnInit && inputValue.length === 0) {
                    fetchApiResult('');
                }
            },
            onSelectedItemChange: ({ selectedItem }) => {
                if (selectedItem && !selectedItems.find(i => i[idKey] === selectedItem[idKey])) {
                    isSingleSelect
                        ? setSelectedItems([selectedItem])
                        : setSelectedItems([...selectedItems, selectedItem]);
                    if (isClearInputValue) {
                        setSearchValue('');
                        reset(); // Clear Downshift’s internal state, including the input value
                    }
                    if (isClearApiResults) {
                        setSearchAPIResult([]);
                    }
                }
            },
        });

    useEffect(() => {
        setSearchValue(initialSearchInputValue);
        setInputValue(initialSearchInputValue);
    }, [initialSearchInputValue]);

    useEffect(() => {
        if (inputValue.length === 0 && !loadDataOnInit) {
            setSearchValue('');
            reset();
            isSingleSelect && setSelectedItems([]);
        }
    }, [inputValue]);

    useEffect(() => {
        if (debounceSearchValue && debounceSearchValue.length >= minSearchQueryLength) {
            fetchApiResult(debounceSearchValue);
        } 
    }, [debounceSearchValue]);

    useEffect(() => {
        if (loadDataOnInit) {
            fetchApiResult('');
        }
    }, [loadDataOnInit]);

    useEffect(() => {
        reset();
    }, [resetTrigger]);

    const fetchApiResult = async (searchValue: string) => {
        try {
            const response = await fetchResults(searchValue);
            setSearchAPIResult(response.length > 0 ? response : []);
        } catch {
            setSearchAPIResult([]);
        }
    };

    return (
        <div className="autocomplete-search-wrapper" id={id}>
            <label className="search-input-label" id={`${id}-label`} htmlFor={`${id}-input`}>
                <ConditionalRendering isTrue={Object.keys(formatMessageObject).length > 0}>
                    <FormattedMessage id={label} values={{ ...formatMessageObject }} />
                </ConditionalRendering>
                <ConditionalRendering isTrue={Object.keys(formatMessageObject).length === 0}>
                    <FormattedMessage id={label} />
                </ConditionalRendering>
            </label>
            <br />
            <input
                {...getInputProps()}
                id={`${id}-input`}
                className="autocomplete-input"
                data-testid={`${id}-input`}
                disabled={disabled}
                placeholder={placeHolder ? intl.formatMessage({ id: placeHolder }) : ''}
            />
            <div
                {...getMenuProps()}
                className="autocomplete-menu"
                data-testid={`${id}-menu`}
                style={{ display: isOpen ? 'block' : 'none' }}
            >
                <ConditionalRendering isTrue={isOpen}>
                    {searchAPIResult.map((item, index) => {
                        const isAlreadyChosen = !!selectedItems.find(i => i[idKey] === item[idKey]);
                        const className = classNames(
                            'autocomplete-item',
                            { highlighted: highlightedIndex === index },
                            { 'not-highlighted': highlightedIndex !== index },
                            { chosen: isAlreadyChosen },
                            { 'not-chosen': !isAlreadyChosen },
                        );
                        return (
                            <div
                                key={item[idKey]}
                                className={className}
                                data-testid={`${id}-menu-item-${index}`}
                                title={item[valueKey]}
                                {...getItemProps({ item, index })}
                            >
                                {item[valueKey]}
                            </div>
                        );
                    })}
                </ConditionalRendering>
            </div>
        </div>
    );
};
export default AutocompleteSearch;
